import React from 'react'

import { Link, type LinkProps } from '@tanstack/react-router'
import { Home } from 'lucide-react'

import { Badge } from '@/components/ui/badge/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb/breadcrumb'

import { cx } from '@/libs/cva'

import { Heading } from '../heading/heading'

type PageBreadcrumbItem = [to: LinkProps['to'], label: string, asText?: boolean]

function PageBreadcrumbs({ items }: { items: PageBreadcrumbItem[] }) {
  const _items = [
    ['/', <Home className="h-3 w-3" key="home" />] as const,
    ...(items ?? []),
  ]
  const len = _items.length

  return (
    <div className={cx('flex h-fit items-center')}>
      <Breadcrumb>
        <BreadcrumbList className="!gap-1">
          {_items.map(([to, label, asText], i) => (
            <React.Fragment key={i}>
              <BreadcrumbItem className="text-xs capitalize">
                {i === len - 1 ? (
                  <BreadcrumbPage>
                    <Badge size="2xsmall">{label}</Badge>
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    {asText ? <span>{label}</span> : <Link to={to}>{label}</Link>}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator className="last:hidden" />
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  )
}

interface PageHeaderProps {
  breadcrumbs?: PageBreadcrumbItem[]
  title?: string
}

function PageHeader({ title, breadcrumbs }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-2 py-2">
      {breadcrumbs && breadcrumbs.length > 0 && <PageBreadcrumbs items={breadcrumbs} />}
      {title && (
        <Heading level="h1" className="font-bold">
          {title}
        </Heading>
      )}
    </div>
  )
}

/**
 * @deprecated
 */
const PageCommon = Object.assign(
  {},
  {
    Breadcrumbs: PageBreadcrumbs,
    Header: PageHeader,
  }
)

export { PageCommon }
